import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function onlyDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

export function formatCnpj(value: string | null | undefined): string {
  const d = onlyDigits(value ?? '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

export function formatCpf(value: string | null | undefined): string {
  const d = onlyDigits(value ?? '').slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export function formatCpfCnpj(value: string | null | undefined): string {
  const d = onlyDigits(value ?? '');
  return d.length > 11 ? formatCnpj(d) : formatCpf(d);
}

export function formatPhone(value: string | null | undefined): string {
  const d = onlyDigits(value ?? '').slice(0, 11);
  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  }
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

export function formatCep(value: string | null | undefined): string {
  const d = onlyDigits(value ?? '').slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, '$1-$2');
}

export function formatCurrency(value: number | string, currency = 'BRL', locale = 'pt-BR'): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
    Number.isFinite(n) ? n : 0,
  );
}

export function formatNumber(value: number, fractionDigits = 0, locale = 'pt-BR'): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatDate(
  value: string | Date | null | undefined,
  pattern = 'dd/MM/yyyy',
): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return format(date, pattern, { locale: ptBR });
}

export function formatDateTime(value: string | Date | null | undefined): string {
  return formatDate(value, "dd/MM/yyyy 'às' HH:mm");
}

export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return formatDistanceToNowStrict(date, { locale: ptBR, addSuffix: true });
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
